import maxBy from 'lodash-es/maxBy';
import { DEFAULT_BORDER_COLOR, undecorateInnerTable } from '../../services/html/table/decorateTable';
import { indexOf } from '../iterable';
import { getBorderLeftWidthForElement, getBorderTopWidthForElement } from '../style/border';
import { isDefined } from '../types';
import { getComputedStyle } from './element';
import { findAncestor, hasAncestor, isElement } from './node';

export function isTableElement(node: Node | null): node is HTMLTableElement {
  return isElement(node) && node.tagName === 'TABLE';
}

export function isTableSectionElement(node: Node | null): node is HTMLTableSectionElement {
  return (
    isElement(node) && (node.tagName === 'THEAD' || node.tagName === 'TBODY' || node.tagName === 'TFOOT')
  );
}

export function isTableHeadElement(node: Node | null): node is HTMLTableSectionElement {
  return isElement(node) && node.tagName === 'THEAD';
}

export function isTableRowElement(node: Node | null): node is HTMLTableRowElement {
  return isElement(node) && node.tagName === 'TR';
}

export function isTableCellElement(node: Node | null): node is HTMLTableCellElement {
  return isElement(node) && (node.tagName === 'TH' || node.tagName === 'TD');
}

export function getFirstColumnCells(table: HTMLTableElement): HTMLTableCellElement[] {
  const firstRow = getFirstNonEmptyRow(table);
  if (!firstRow) {
    return [];
  }
  const firstCell = firstRow.querySelector('td:first-child, th:first-child');
  return getVerticalAlignedCells(table, firstCell as HTMLTableCellElement).reduce((acc, cells) => {
    const [cell] = cells;
    if (!acc.includes(cell)) {
      return acc.concat(cell);
    }
    return acc;
  });
}

export function getVerticalAlignedCells(table: HTMLTableElement, refCell: HTMLTableCellElement): TablePlan {
  const tablePlan = getTablePlan(table);
  const cellCoordinate = getCellCoordinateInPlan(refCell, tablePlan);
  if (!cellCoordinate) {
    return [[refCell]];
  }

  const cells: TablePlan = [];
  for (let r = 0; r < tablePlan.length; r++) {
    const row: HTMLTableCellElement[] = [];
    cells.push(row);
    for (let c = cellCoordinate.column; c < cellCoordinate.column + refCell.colSpan; c++) {
      const cell = tablePlan[r][c];
      if (cell !== undefined) {
        row.push(cell);
      }
    }
  }
  return cells;
}

export function getHorizontalAlignedCells(table: HTMLTableElement, refCell: HTMLTableCellElement) {
  const tablePlan = getTablePlan(table);
  const cellCoordinate = getCellCoordinateInPlan(refCell, tablePlan);
  if (!cellCoordinate) {
    return [[refCell]];
  }

  const cells: TablePlan = [];
  for (let r = cellCoordinate.row; r < cellCoordinate.row + refCell.rowSpan; r++) {
    cells.push(tablePlan[r].filter(cell => cell !== undefined));
  }
  return cells;
}

export function getMaxBorderTopWidth(table: HTMLTableElement) {
  if (!table) {
    return null;
  }
  const firstRow = getFirstNonEmptyRow(table); // Will 'break' if table's first row is empty yet has a big borderTop.
  if (!firstRow) {
    return null;
  }
  const elements: Element[] = [firstRow, ...Array.from(firstRow.children).filter(isTableCellElement)];
  let parent = firstRow.parentElement;
  while (parent !== null && parent !== table.parentElement) {
    if (isTableElement(parent) || isTableSectionElement(parent)) {
      elements.push(parent);
    }
    parent = parent.parentElement;
  }
  return Math.max.apply(
    null,
    elements.map(el => {
      const borderTopWidth = getBorderTopWidthForElement(el);
      return borderTopWidth === null ? NaN : borderTopWidth;
    }),
  );
}

export function getMaxBorderLeftWidth(table: HTMLTableElement) {
  if (!table) {
    return null;
  }
  const cells = getFirstColumnCells(table);
  return Math.max.apply(
    null,
    cells
      .map(cell => {
        const elements: Element[] = [cell];
        let parent = cell.parentElement;
        while (parent !== null && parent !== table.parentElement) {
          if (isTableElement(parent) || isTableSectionElement(parent) || isTableRowElement(parent)) {
            elements.push(parent);
          }
          parent = parent.parentElement;
        }
        return elements;
      })
      .reduce((acc, elements) => {
        return acc.concat(elements);
      }, [])
      .map(el => {
        const borderLefWidth = getBorderLeftWidthForElement(el);
        return borderLefWidth === null ? NaN : borderLefWidth;
      }),
  );
}

export function getFirstNonEmptyRow(table: HTMLTableElement) {
  const { rows } = table;
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (Array.from(row.children).find(isTableCellElement) !== null) {
      return row;
    }
  }
  return null;
}

/*
 * Computing visual content of a column/row, or visual neighbors of a cell gets complicated
 * when the table contains cells of dimensions greater than 1*1.
 * Therefore we give ourselves a model representation of the table that makes all those computations.
 * /!\ This model does not get updated when the table mutated.
 */
/**
 * A TablePlan is a matrix representation of a table or of a group of cells:
 * - Each of its subarray are matrix's row.
 * - Each of the subarray items are matrix's cell.
 * - Each matrices's cell points to a TableCell.
 * It provides the following guarantees:
 * - Each cell of the matrix represents a TableCell or a part of a TableCell. A TableCell of dimension n*m greater than 1*1 will occupy n*m matrix's cell.
 * - Horizontal order is respected: consecutive matrix's cell represent consecutive TableCells, in the same order.
 * - Vertical alignment is respected.
 */
export type TablePlan = HTMLTableCellElement[][];

/**
 * Return the tablePlan matching table.
 * @param canOverflow will determine weather a spreading cell can create an more rows/columns in tablePlan than in its original table/section.
 * - By default, when displayed in browsers, cells cannot spread beyond their table/section number of rows/columns.
 *   For instance: If a table contains only one row, and a cell which rowspan > 1, it will be ignore and the table
 *   will keep its original number of rows. To reflect this behavior in the corresponding tablePlan, canOverflow must be false.
 * - However, when copy/pasting, cells spreading on several rows, the user will want to paste what he saw.
 *   In such case, if copying one cell spreading on two existing rows from a table, the number of rows in the clipboard will only be one.
 *   Yet we want the tablePlan reflecting the clipboard content to capture the information that this cell originally spread on two rows.
 *   In order to do that, the spreading cell is allowed to created more rows in the tablePlan than the number of rows in its table by
 *   setting canOverlow to true.
 */
export function getTablePlan(table: HTMLTableElement, canOverflow?: boolean): TablePlan {
  const tablePlan: TablePlan = [];
  const { rows } = table;
  // Iterating over table's rows.
  for (let rowIndex = 0; rowIndex < table.rows.length; rowIndex++) {
    const row = rows[rowIndex];
    // Retrieve rowPlan for this row if it was created due to a previous cell spreading on several rows.
    let rowPlan = tablePlan[rowIndex];
    // Create one otherwise.
    if (!rowPlan) {
      rowPlan = [];
      tablePlan[rowIndex] = rowPlan;
    }
    const { cells } = row;
    // Iterating over row's cells.
    for (let colIndex = 0; colIndex < cells.length; colIndex++) {
      const cell = cells[colIndex];
      const { rowSpan, colSpan } = cell;
      // Find first free spot in row
      // (might not be the one at colIndex if it got already occupied by a cell spreading on several row/columns).
      const firstFreeColumn = getFistFreeColumnIndex(rowPlan);
      // If cell spreads on several columns / rows we fill the corresponding squares in the matrix.
      for (let r = rowIndex; r < rowIndex + rowSpan; r++) {
        // A cell cannot spread beyond its tableSection, nor the number of rows of table.
        if (!canOverflow && (r >= rows.length || rows[r].parentElement !== row.parentElement)) {
          continue;
        }
        for (let c = firstFreeColumn; c < firstFreeColumn + colSpan; c++) {
          // Retrieve rowPlan for at index r or creating it ahead of time.
          let filledRowMap = tablePlan[r];
          if (!filledRowMap) {
            filledRowMap = [];
            tablePlan[r] = filledRowMap;
          }
          // Fill cell at position [r,c].
          filledRowMap[c] = cell;
        }
      }
    }
  }
  return tablePlan;
}

/**
 * Returns the TablePlan matching the subtable defined by current selection in a table.
 * This subtable includes all cells that are:
 * - at same row or bellow anchorNode
 * - at same row or above focusNode
 * - at same column or right to anchorNode
 * - at same column or left to focusNode
 * There is no such subtable if:
 * - anchorNode or focusNode are not on a HTMLTableCellElement,
 * - anchorNode or focusNode are not in the same HTMLTableElement
 */
export function getSelectedCellsPlan(element: Element, range: Range) {
  // If no selection, do nothing.
  if (range.collapsed) {
    return null;
  }
  const { commonAncestorContainer, startContainer, endContainer } = range;

  // If selection not within a table, do nothing.
  if (
    !isTableElement(commonAncestorContainer) &&
    !isTableSectionElement(commonAncestorContainer) &&
    !isTableRowElement(commonAncestorContainer)
  ) {
    return null;
  }
  const table = isTableElement(commonAncestorContainer)
    ? commonAncestorContainer
    : findAncestor(commonAncestorContainer, isTableElement, element);
  if (!table) {
    return null; // We should never get there because of the check we did on commonAncestorContainer.
  }
  const startCell = findAncestor(startContainer, isTableCellElement, element);
  const endCell = findAncestor(endContainer, isTableCellElement, element);
  if (!startCell || !endCell) {
    return null; // We should never get there as it's not normally possible to end with a cursor outside of a cell in a table.
  }

  // Create selectionPlan, it is a tablePlan containing all cells contained in rectangle defined by startCell and endCell.
  const tablePlan = getTablePlan(table);
  const startCoordinate = getCellCoordinateInPlan(startCell, tablePlan);
  const endCoordinate = getCellCoordinateInPlan(endCell, tablePlan);
  if (!startCoordinate || !endCoordinate) {
    return null; // We should never get there either as both cells come from table.
  }
  const [minRow, maxRow] = [startCoordinate, endCoordinate].map(c => c.row).sort((a, b) => a - b);
  const [minColumn, maxColumn] = [startCoordinate, endCoordinate].map(c => c.column).sort((a, b) => a - b);
  const selectionPlan = tablePlan
    .map((rowPlan, rowIndex) =>
      rowPlan.filter(
        (cell, columnIndex) =>
          minRow <= rowIndex && rowIndex <= maxRow && minColumn <= columnIndex && columnIndex <= maxColumn,
      ),
    )
    .filter(selectionRowPlan => selectionRowPlan.length > 0);
  return selectionPlan;
}

/**
 * Return table whose content is the same as in tablePlan.
 */
export function getTableFromTablePlan(tablePlan: TablePlan) {
  const firstCellInPlan = getFirstCellInPlan(tablePlan);
  if (!firstCellInPlan) {
    return null;
  }
  const document = firstCellInPlan.ownerDocument;
  if (!document) {
    return null;
  }
  // Create new hidden table and append it to tableWrapper.
  const selectionTable = document.createElement('table');
  // Keep track of appended cells to prevent adding the spreading ones twice.
  const appendedCells = new Set<HTMLTableCellElement>();
  tablePlan.forEach(rowPlan => {
    const row = document.createElement('tr');
    selectionTable.append(row);
    rowPlan.forEach(cell => {
      if (!appendedCells.has(cell)) {
        row.appendChild(cell.cloneNode(true));
        appendedCells.add(cell);
      }
    });
  });
  // We do not want to include any decorators in this table.
  undecorateInnerTable(selectionTable);
  return selectionTable;
}

function getFistFreeColumnIndex<T>(row: T[]) {
  let outOfRange = false;
  let index = 0;
  while (!outOfRange) {
    if (index >= row.length) {
      outOfRange = true;
      break;
    }
    if (!row[index]) {
      return index;
    }
    index += 1;
  }
  return index;
}

export interface TableCellCoordinate {
  row: number;
  column: number;
}

export function getCellCoordinateInPlan(
  cell: HTMLTableCellElement,
  tablePlan: TablePlan,
): TableCellCoordinate | null {
  for (let r = 0; r < tablePlan.length; r++) {
    const rowPlan = tablePlan[r];
    for (let c = 0; c < rowPlan.length; c++) {
      const currentCell = rowPlan[c];
      if (cell === currentCell) {
        return { row: r, column: c };
      }
    }
  }
  return null;
}

export function getCellCountInPlan(tablePlan: TablePlan) {
  return tablePlan.map(rowPlan => rowPlan.length).reduce((acc, length) => acc + length, 0);
}

function getLongestRowInPlan(tablePlan: TablePlan) {
  return maxBy(tablePlan, row => row.length);
}

export function getFirstCellInPlan(tablePlan: TablePlan) {
  const firstNonEmptyRow = tablePlan.filter(row => row.length !== 0)[0];
  if (!firstNonEmptyRow) {
    return null;
  }
  const firstNonEmptyRowFiltered = firstNonEmptyRow.filter(isDefined);
  return firstNonEmptyRowFiltered[0];
}

export function getLastCellInPlan(tablePlan: TablePlan) {
  const lastNonEmptyRow = tablePlan.filter(row => row.length !== 0).pop();
  if (!lastNonEmptyRow) {
    return null;
  }
  const lastNonEmptyRowFiltered = lastNonEmptyRow.filter(cell => cell !== undefined);
  return lastNonEmptyRowFiltered.pop() as HTMLTableCellElement;
}

export function isSameTablePlan(tp1: TablePlan | null, tp2: TablePlan | null) {
  if (tp1 === null) {
    return tp2 === null;
  }
  if (tp2 === null) {
    return false;
  }
  // TablePlan are the same if one set of cells is included in the other and they have same cardinality.
  const prevSelectionPlanList = tp1.reduce((acc, rowPlan) => acc.concat(rowPlan), []);
  const newSelectionPlanList = tp2.reduce((acc, rowPlan) => acc.concat(rowPlan), []);
  return (
    prevSelectionPlanList.filter(cell => !newSelectionPlanList.includes(cell)).length === 0 &&
    prevSelectionPlanList.length === newSelectionPlanList.length
  );
}

/**
 * Return an empty row having the same style as row.
 */
function getEmptyRow(row: HTMLTableRowElement) {
  const { ownerDocument: document } = row;
  if (!document) {
    return null;
  }
  const newRow = document.createElement('tr');
  const style = row.getAttribute('style');
  if (style) {
    newRow.setAttribute('style', style);
  }
  return newRow;
}

/**
 * Add an empty row in table above refCell.
 */
export function addRowAboveCell(table: HTMLTableElement, refCell: HTMLTableCellElement) {
  addRowBesideCell(table, refCell, 'up');
}

/**
 * Add an empty row in table under refCell.
 */
export function addRowUnderCell(table: HTMLTableElement, refCell: HTMLTableCellElement) {
  addRowBesideCell(table, refCell, 'down');
}

/**
 * Add an empty row in table above or bellow refCell.
 */
function addRowBesideCell(table: HTMLTableElement, refCell: HTMLTableCellElement, side: 'up' | 'down') {
  const rowPlan = getHorizontalAlignedCells(table, refCell);
  // Retrieve refCell's row in DOM.
  const originalRow =
    side === 'up'
      ? findAncestor(rowPlan[0][0], isTableRowElement, table)
      : getLowestRowInPlan(table, rowPlan);
  if (!originalRow) {
    return;
  }
  // Retrieve the element to which we'll append the new row.
  const { parentElement: parentContainer, nextSibling: nextRow } = originalRow;
  if (!parentContainer) {
    return;
  }
  // Let's get ourselves a new row.
  const newRow = getEmptyRow(originalRow);
  if (!newRow) {
    return;
  }
  // Retrieve side row's plan to identify cells spreading between it and refCell's row.
  const originalSideRowPlan =
    side === 'up'
      ? getUpperRowPlanInSection(table, originalRow)
      : getLowerRowPlanInSection(table, originalRow);
  // Iterate through either rowPlan's first or last row to fill new row (rowPlan can include several rows is refCell's rowSpan > 1).
  const rowToFollow = rowPlan[side === 'up' ? 0 : rowPlan.length - 1];
  for (let i = 0; i < rowToFollow.length; i++) {
    const cell = rowToFollow[i];
    // If a cell was spreading from upper row, it will spread through new added row too.
    if (cell.rowSpan > 1 && originalSideRowPlan !== null && originalSideRowPlan[i] === cell) {
      const newRowSpan = cell.rowSpan + 1;
      cell.setAttribute('rowspan', newRowSpan.toString());
      continue;
    }
    // Otherwise we append a cell to the new row.
    const newCell = getNewCellFromCell(cell);
    if (newCell) {
      newRow.appendChild(newCell);
    }
  }
  // Add new row to table.
  switch (side) {
    case 'up':
      parentContainer.insertBefore(newRow, originalRow);
      break;
    case 'down':
      if (nextRow) {
        parentContainer.insertBefore(newRow, nextRow);
      } else {
        parentContainer.appendChild(newRow);
      }
      break;
    default:
      break;
  }
}

function getUpperRowPlanInSection(table: HTMLTableElement, row: HTMLTableRowElement) {
  const rows = Array.from(table.rows);
  const originalRowAboveIndex = indexOf(rows, row) - 1;
  const originalRowAbove = originalRowAboveIndex >= 0 ? rows[originalRowAboveIndex] : null;
  if (originalRowAbove !== null && originalRowAbove.parentElement === row.parentElement) {
    const tablePlan = getTablePlan(table);
    return tablePlan[originalRowAboveIndex];
  }
  return null;
}

function getLowerRowPlanInSection(table: HTMLTableElement, row: HTMLTableRowElement) {
  const rows = Array.from(table.rows);
  const originalRowAboveIndex = indexOf(rows, row) + 1;
  const originalRowAbove = originalRowAboveIndex < rows.length ? rows[originalRowAboveIndex] : null;
  if (originalRowAbove !== null && originalRowAbove.parentElement === row.parentElement) {
    const tablePlan = getTablePlan(table);
    return tablePlan[originalRowAboveIndex];
  }
  return null;
}

function getLowestRowInPlan(table: HTMLTableElement, plan: TablePlan): HTMLTableRowElement | undefined {
  const { rows } = table;
  if (!rows) {
    return undefined;
  }
  const rowsArr = Array.from(rows);
  return (
    plan
      // Flatten plan to get its cells only.
      .reduce((acc: HTMLTableCellElement[], r) => acc.concat(r))
      // For each cell, find its row, add it to acc if it does not already have it.
      .reduce((acc: HTMLTableRowElement[], cell) => {
        const row = findAncestor(cell, isTableRowElement, table);
        if (row != null && !acc.includes(row)) {
          acc.push(row);
        }
        return acc;
      }, [])
      // Sort those rows by index in table.
      .sort((r1, r2) => {
        return rowsArr.indexOf(r1) - rowsArr.indexOf(r2);
      })
      // Last one should be the lowest.
      .pop()
  );
}

/**
 * Add an empty column in table at the left of refCell.
 */
export function addColumnLeftToCell(table: HTMLTableElement, refCell: HTMLTableCellElement) {
  addColumnBesideCell(table, refCell, 'left');
}

/**
 * Add an empty column in table at the right of refCell.
 */
export function addColumnRightToCell(table: HTMLTableElement, refCell: HTMLTableCellElement) {
  addColumnBesideCell(table, refCell, 'right');
}

/**
 * Add an empty column in table at a side of refCell (left or right).
 */
function addColumnBesideCell(table: HTMLTableElement, refCell: HTMLTableCellElement, side: 'left' | 'right') {
  if (!hasConsistentColgroup(table)) {
    generateColgroup(table);
  }
  const tablePlan = getTablePlan(table);
  const originalCoordinate = getCellCoordinateInPlan(refCell, tablePlan);
  if (originalCoordinate === null) {
    return;
  }
  const columnPlan = getVerticalAlignedCells(table, refCell);
  // Retrieve original side colPlan to identify cells spreading from it to refCell's col.
  const originalSideCell = side === 'left' ? refCell.previousElementSibling : refCell.nextElementSibling;
  const originalSideColumn =
    originalSideCell !== null
      ? getVerticalAlignedCells(table, originalSideCell as HTMLTableCellElement)
      : null;
  // Keep track of cells that were used to create new empty cells.
  const usedCells = new Set<HTMLTableCellElement>();
  let didAddCell = false;
  // Iterate through column's rows.
  columnPlan.forEach((rowPlan, index) => {
    // Leave empty rows as they are (as they can be used as separators).
    const { length: rowLength } = rowPlan;
    if (rowLength < 1) {
      return;
    }
    // Retrieve first of last cell of current row depending on side.
    // (there can be several cells in row if refCell spreads on several columns).
    const cell = rowPlan[side === 'left' ? 0 : rowLength - 1];
    // If the cell has already been used to create a new one it means it spreads on several rows.
    // We only want to take it into consideration once since we already created a new one with the same rowspan.
    if (usedCells.has(cell)) {
      return;
    }
    const { parentElement: parentRow, nextElementSibling: nextCell } = cell;
    if (!parentRow) {
      return;
    }
    // If a cell was spreading from side column to refCell's column, it will spread through new added column too.
    if (cell.colSpan > 1 && originalSideColumn) {
      const currentRowSideCell =
        originalSideColumn[index][side === 'left' ? originalSideColumn[index].length - 1 : 0];
      if (currentRowSideCell === cell) {
        cell.setAttribute('colspan', (cell.colSpan + 1).toString());
        return;
      }
    }
    // Otherwise let's add a new cell.
    const newCell = getNewCellFromCell(cell);
    if (!newCell) {
      return;
    }
    // With same rowSpan as the original.
    if (cell.rowSpan > 1) {
      newCell.setAttribute('rowspan', cell.rowSpan.toString());
    }
    // Adding the new cell.
    switch (side) {
      case 'left':
        parentRow.insertBefore(newCell, cell);
        break;
      case 'right':
        if (nextCell) {
          parentRow.insertBefore(newCell, nextCell);
        } else {
          parentRow.appendChild(newCell);
        }
        break;
      default:
        break;
    }
    usedCells.add(cell);
    didAddCell = true;
  });
  if (didAddCell) {
    appendColToColgroup(
      table,
      side === 'left' ? originalCoordinate.column : originalCoordinate.column + refCell.colSpan,
    );
  }
}

/**
 * Remove each cell contained in tableSectionPlan from table.
 */
export function deleteCells(table: HTMLTableElement, tableSectionPlan: TablePlan) {
  tableSectionPlan.forEach(rowPlan => {
    rowPlan.forEach((cell, index) => {
      if (index === rowPlan.length - 1) {
        const rowParent = findAncestor(cell, isTableRowElement, table);
        const sectionParent = findAncestor(cell, isTableSectionElement, table);
        cell.remove();
        if (rowParent && rowParent.children.length === 0) {
          rowParent.remove();
        }
        if (sectionParent && sectionParent.children.length === 0) {
          sectionParent.remove();
        }
      } else {
        cell.remove();
      }
    });
  });
  return true;
}

/**
 * Mutates table so that all rows have the same length by appending empty cells to them.
 * A row's dimension is the sum of the colSpan value of its cells.
 */
export function makeTableRectangle(table: HTMLTableElement) {
  const document = table.ownerDocument;
  if (!document) {
    return;
  }
  const tablePlan = getTablePlan(table);
  const maxNbCols = Math.max(...tablePlan.map(row => row.length));
  Array.from(table.rows).forEach((row, index) => {
    if (row.cells.length === 0) {
      return; // Do not normalize empty row as they can be used as separator.
    }
    const nbMissingCells = maxNbCols - getTablePlan(table)[index].length;
    if (nbMissingCells === 0) {
      return;
    }
    const newCell = getNewCell(table, document, isTableHeadElement(row) ? 'th' : 'td');
    for (let i = 0; i < nbMissingCells; i++) {
      row.appendChild(newCell.cloneNode(true));
    }
  });
}

/**
 * Returns a <br/> node wraps within all the wrapper that were found in element.
 * An element is a wrapper is it has no element sibling and no sibling with text content.
 * @param element
 * @param document
 */
function getSameContentWrapperAs(element: Element, document: Document) {
  const { firstElementChild: maybeWrapper } = element;
  if (
    // We want only one node with non empty text content.
    Array.from(element.childNodes).filter(node => !node.textContent || node.textContent.trim().length > 0)
      .length <= 1 &&
    // We want only one child element, being a div or a span.
    element.children.length === 1 &&
    maybeWrapper &&
    ['DIV', 'SPAN', 'B'].includes(maybeWrapper.tagName)
  ) {
    const innerWrapper = maybeWrapper.cloneNode() as Element;
    innerWrapper.append(getSameContentWrapperAs(maybeWrapper, document));
    return innerWrapper;
  }
  return document.createElement('br');
}

/**
 * Empty a cell by leaving its original content wrapper and replacing its content by a <br/> tag.
 * @param cell
 * @param document
 */
function emptyCell(cell: HTMLTableCellElement, document: Document) {
  const newContentWrapper = getSameContentWrapperAs(cell, document);
  cell.childNodes.forEach(child => child.remove());
  cell.appendChild(newContentWrapper);
}

export function emptyCells(cells: TablePlan) {
  const firstCell = cells[0][0];
  const document = firstCell.ownerDocument;
  if (!document) {
    return;
  }
  cells.forEach(row => {
    row.forEach(cell => {
      emptyCell(cell, document);
    });
  });
}

/**
 * Return an empty cell having the same style as cell.
 */
function getNewCellFromCell(cell: HTMLTableCellElement) {
  const { ownerDocument: document } = cell;
  if (!document) {
    return null;
  }
  const newCell = document.createElement(cell.tagName) as HTMLTableCellElement;
  newCell.append(getSameContentWrapperAs(cell, document));
  const style = cell.getAttribute('style');
  if (style) {
    newCell.setAttribute('style', style);
  }
  maybeFixCellBorder(newCell as HTMLTableCellElement, getComputedStyle(cell));
  return newCell;
}

/**
 * Return an empty cell, whose style will try to match other similar cells in table (depending on if it's a td/th).
 * If no style is found, defaultStyle will be applied.
 */
function getNewCell(table: HTMLTableElement, document: Document, cellTag: 'th' | 'td') {
  // Let's try to find some cell to use as a model.
  const modeCells = table.querySelectorAll(`${cellTag}`);
  const modelCell = modeCells[modeCells.length - 1] as HTMLTableCellElement;
  if (modelCell) {
    const newCell = getNewCellFromCell(modelCell);
    if (newCell) {
      return newCell;
    }
  }
  // If none were found, create a new one from scratch.
  const newCell = document.createElement(cellTag);
  newCell.innerHTML = '<br/>';
  maybeFixCellBorder(newCell, getComputedStyle(newCell));
  return newCell as HTMLTableCellElement;
}

/**
 * Make sure cell has border.
 * Uses some provided computedStyle instead of computing cell's style
 * because this function might be called after cell is created but before it get appended to DOM.
 * In such case, computing style on cell would only gives empty strings,
 * computedStyle parameter enable to provide some other cell's style used as model.
 */
export function maybeFixCellBorder(cell: HTMLTableCellElement, computedStyle: CSSStyleDeclaration | null) {
  const { style } = cell;
  if (computedStyle !== null) {
    const { borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth } = computedStyle;
    if (!computedStyle.borderColor) {
      style.setProperty('border-color', DEFAULT_BORDER_COLOR);
    }
    if (!borderTopWidth || borderTopWidth.startsWith('0')) {
      style.setProperty('border-top-width', '1px');
    }
    if (!borderRightWidth || borderRightWidth.startsWith('0')) {
      style.setProperty('border-right-width', '1px');
    }
    if (!borderBottomWidth || borderBottomWidth.startsWith('0')) {
      style.setProperty('border-bottom-width', '1px');
    }
    if (!borderLeftWidth || borderLeftWidth.startsWith('0')) {
      style.setProperty('border-left-width', '1px');
    }
  }
}

/**
 * Append a new row to table filled with nbEmptyCells empty cells.
 */
export function appendNewRowToTable(table: HTMLTableElement, nbEmptyCells: number, document: Document) {
  const row = document.createElement('tr');
  for (let i = 0; i < nbEmptyCells; i++) {
    const newCell = getNewCell(table, document, 'td');
    row.appendChild(newCell);
  }
  if (isTableSectionElement(table.lastElementChild)) {
    table.lastElementChild.append(row);
  } else {
    table.append(row);
  }
  return row;
}

/**
 * Append a new cell to row.
 */
export function appendNewCellToRow(table: HTMLTableElement, row: HTMLTableRowElement, document: Document) {
  const tagName = hasAncestor(row, isTableHeadElement, table) ? 'th' : 'td';
  const newCell = getNewCell(table, document, tagName);
  row.appendChild(newCell);
  return newCell;
}

export function addCellBefore(table: HTMLTableElement, cell: HTMLTableCellElement) {
  const row = findAncestor(cell, isTableRowElement, table);
  if (!row) {
    return null;
  }
  const newCell = getNewCellFromCell(cell);
  if (!newCell) {
    return null;
  }
  row.insertBefore(newCell, cell);
  return newCell;
}

export function addCellAfter(table: HTMLTableElement, cell: HTMLTableCellElement) {
  const { nextSibling } = cell;
  if (nextSibling) {
    return addCellBefore(table, nextSibling as HTMLTableCellElement);
  }
  const row = findAncestor(cell, isTableRowElement, table);
  if (!row) {
    return null;
  }
  const newCell = getNewCellFromCell(cell);
  if (!newCell) {
    return null;
  }
  row.append(newCell);
  return newCell;
}

export function insertNewCellAtCoordinate(
  table: HTMLTableElement,
  document: Document,
  coordinate: TableCellCoordinate,
  rowSpan: number,
  colSpan: number,
) {
  const { row: rowIndex, column: colIndex } = coordinate;
  const tablePlan = getTablePlan(table);
  const rowPlan = tablePlan[rowIndex];
  const row = table.rows[rowIndex];
  if (!rowPlan || !row) {
    return null;
  }
  const isTableHead = row.firstElementChild !== null && row.firstElementChild.tagName === 'TH';
  const newCell = getNewCell(table, document, isTableHead ? 'th' : 'td');
  newCell.setAttribute('colspan', colSpan.toString());
  newCell.setAttribute('rowspan', rowSpan.toString());
  let anchorCell: HTMLTableCellElement | undefined;
  for (let i = colIndex; i < rowPlan.length; i++) {
    const maybeAnchorCell = rowPlan[i];
    if (findAncestor(maybeAnchorCell, isTableRowElement, table) === row) {
      anchorCell = maybeAnchorCell;
      break;
    }
  }
  if (anchorCell) {
    row.insertBefore(newCell, anchorCell);
  } else {
    row.appendChild(newCell);
  }
  return newCell;
}

function generateColgroup(table: HTMLTableElement) {
  const document = table.ownerDocument;
  if (!document) {
    return;
  }
  // Compute new colgroup.
  const colgroup = document.createElement('colgroup');
  const tablePlan = getTablePlan(table);
  const longestRowPlan = getLongestRowInPlan(tablePlan);
  if (!longestRowPlan) {
    return;
  }
  longestRowPlan.forEach(cell => {
    const col = document.createElement('col');
    const width = cell.getBoundingClientRect().width / cell.colSpan;
    col.setAttribute('width', `${width}px`);
    colgroup.append(col);
  });
  // Remove previous ones if any.
  table.querySelectorAll('colgroup').forEach(cg => cg.remove());
  // Add new colgroup.
  table.prepend(colgroup);
}

function hasColgroup(table: HTMLTableElement) {
  return table.querySelector('colgroup') !== null;
}

function hasConsistentColgroup(table: HTMLTableElement) {
  const colgroups = table.querySelectorAll('colgroup');
  if (colgroups.length !== 1) {
    return false;
  }
  const [colgroup] = colgroups;
  const tablePlan = getTablePlan(table);
  const longestRow = getLongestRowInPlan(tablePlan);
  if (!longestRow) {
    return false;
  }
  const cols = colgroup.querySelectorAll('col');
  for (let i = 0; i < cols.length; i++) {
    const col = cols[i] as HTMLTableColElement;
    if (col.span > 1) {
      return false;
    }
    if (col.width === null) {
      return false;
    }
  }
  return cols.length !== longestRow.length;
}

// Add or fix any existing colgroup
export function fixColgroup(table: HTMLTableElement) {
  if (!hasConsistentColgroup(table)) {
    generateColgroup(table);
  }
}

// Fixes the colgroup if one is present, otherwise do nothing
export function maybeFixColgroup(table: HTMLTableElement) {
  if (!hasColgroup(table)) {
    return;
  }

  fixColgroup(table);
}

function appendColToColgroup(table: HTMLTableElement, index: number) {
  const document = table.ownerDocument;
  if (!document) {
    return;
  }
  const colgroups = table.querySelectorAll('colgroup');
  if (colgroups.length !== 1) {
    return;
  }
  const [colgroup] = colgroups;
  const cols = colgroup.querySelectorAll('col');
  if (cols.length < index) {
    return;
  }
  const nextCol = cols[index];
  const refCol = nextCol || cols[cols.length - 1];
  const newCol = document.createElement('col');
  const avgWidth = table.getBoundingClientRect().width / cols.length;
  newCol.setAttribute('width', refCol.getAttribute('width') || `${avgWidth}px`);
  if (nextCol) {
    colgroup.insertBefore(newCol, nextCol);
  } else {
    colgroup.append(newCol);
  }
}
